import React, { useState, useCallback, useMemo } from 'react';
import { Button, ReactDropdown, Loader } from '@storybook';
import Modal from '@storybook/new-modal/modal';
import { 
	ITimezoneState, 
	ITimezoneOption,
	TIMEZONE_OPTIONS,
	getCurrentSystemTimezone,
	findTimezoneByValue,
	createTimezoneState
} from 'global-stores/timezone';

interface TimezoneModalProps {
	isOpen: boolean;
	onClose: () => void;
	currentTimezone: ITimezoneState;
	onTimezoneChange: (timezone: ITimezoneState) => void;
	loading?: boolean;
}

export const TimezoneModal: React.FC<TimezoneModalProps> = ({
	isOpen,
	onClose,
	currentTimezone,
	onTimezoneChange,
	loading = false,
}) => {
	const [selectedTimezone, setSelectedTimezone] = useState<ITimezoneState>(currentTimezone);

	// Get current timezone automatically
	const getCurrentTimezone = useCallback(() => {
		const systemTimezone = getCurrentSystemTimezone();
		const currentOption = findTimezoneByValue(TIMEZONE_OPTIONS, systemTimezone);
		
		if (currentOption) {
			const newTimezone = createTimezoneState(currentOption);
			setSelectedTimezone(newTimezone);
		}
	}, []);

	const handleTimezoneSelect = useCallback((option: ITimezoneOption | null) => {
		if (option) {
			const newTimezone = createTimezoneState(option);
			setSelectedTimezone(newTimezone);
		}
	}, []);

	const handleSave = useCallback(() => {
		onTimezoneChange(selectedTimezone);
	}, [selectedTimezone, onTimezoneChange]);

	const handleCancel = useCallback(() => {
		setSelectedTimezone(currentTimezone);
		onClose();
	}, [currentTimezone, onClose]);

	// Find current selected option for dropdown
	const currentDropdownValue = useMemo(() => 
		TIMEZONE_OPTIONS.find(option => option.value === selectedTimezone.timezone),
		[selectedTimezone.timezone]
	);

	const modalTitle = useMemo(() => (
		<div className="timezone-modal__header">
			<div className="timezone-modal__title">
				Time Zone
			</div>
			<div className="timezone-modal__description">
				Set your preferred time zone for reports, documents and notifications.
			</div>
		</div>
	), []);

	return (
		<Modal
			isOpen={isOpen}
			modalName="Timezone Settings"
			closeModal={handleCancel}
			className="timezone-modal"
			showCloseBtn={true}
			isStopOutsideClick={false}
			title={modalTitle}
		>
			<div className="timezone-modal-body">
				<div className="timezone-modal-body__dropdown">
					<div className="timezone-modal-body__form-row">
						<label className="timezone-modal-body__label">
							Select Your Preferred Time Zone
						</label>
						<button
							type="button"
							className="timezone-modal-body__auto-detect"
							onClick={getCurrentTimezone}
							disabled={loading}
						>
							Use Current Time Zone
						</button>
					</div>
					<ReactDropdown
						options={TIMEZONE_OPTIONS}
						value={currentDropdownValue}
						handleChangeSelect={handleTimezoneSelect}
						placeholder="Select a timezone..."
						isSearchable={true}
						Isdisable={loading}
						controlStyle={{
							backgroundColor: '#F5F8FF',
							border: '1px solid #0000001F',
							borderRadius: 4,
							minHeight: 48,
							fontSize: 12,
							fontWeight: 400,
							color: '#363A45',
							'&:hover': {
								borderColor: '#9ca3af',
							},
							'&:focus': {
								borderColor: '#3b82f6',
								boxShadow: '0 0 0 1px #3b82f6',
							}
						}}
						optionsStyle={{
							fontSize: 14,
                            color: '#363A45',
                            backgroundColor: '#F5F8FF',
							fontWeight: 400,
							'&:hover': {
								color: '#fff',
								backgroundColor: '#3b82f6',
							}
						}}
					/>
				</div>
			</div>
            <div className="timezone-modal-footer">
					<Button
						label="Cancel"
						type="button button__filled button__filled--secondary"
						handleClick={handleCancel}
						disabled={loading}
					/>
					<Button
						label={loading ? 
							<Loader type="loader" className="loader-white" dimension={16} /> : 
							"Save"
						}
						type="button__filled--primary"
						handleClick={handleSave}
						disabled={!selectedTimezone.timezone || loading}
					/>
				</div>
		</Modal>
	);
};
